import React from "react";
import InputField from "./InputField";
import CheckBoxField from "../CheckBoxField/CheckBoxField";
import { SUSPENSE_WRAPPER } from "../SuspenseWrapper/SuspenseWrapper";
import { DynamicMenuOptions, Field } from "../../../Interface/scheduler/form";
import { useFormContext } from "../../contexts/FormContext";
import { useBusinessNameVisibility } from "../../hooks/useBusinessNameVisibility";
import { AppConstants } from "../../../constants/constant";
import RadioGroupHandler from "../RadioGroup/RadioGroupHandler";
import NextArrowField from "../NextArrow/NextArrowField";

const LazyRenderSelect = React.lazy(
  () =>
    import(
      /* webpackChunkName: "scheduler/ServicePage" */ "../SelectField/SelectField"
    )
);
const LazyRenderTextArea = React.lazy(
  () =>
    import(/* webpackChunkName: "scheduler/ServicePage" */ "./TextFeildArea")
);
const LazyRenderUploadFile = React.lazy(
  () =>
    import(
      /* webpackChunkName: "scheduler/ServicePage" */ "../UploadFields/UploadFieldComponent"
    )
);
const LazyRenderImageUploadFile = React.lazy(
  () =>
    import(
      /* webpackChunkName: "scheduler/ServicePage" */ "../UploadFields/ImageUploader"
    )
);
const LazyRenderRadioGroupImage = React.lazy(
  () =>
    import(
      /* webpackChunkName: "scheduler/ServicePage" */ "../RadiouGroupImage/RadioGroupImage"
    )
);
const LazyRenderCheckBoxGroup = React.lazy(
  () =>
    import(
      /* webpackChunkName: "scheduler/ServicePage" */ "../CheckboxGroup/CheckboxGroup"
    )
);

const LazyRenderCheckBoxGroupIcon = React.lazy(
  () =>
    import(
      /* webpackChunkName: "scheduler/ServicePage" */ "../CheckBoxGroupIcon/CheckBoxGroupIcon"
    )
);

const LazyRenderAutoSelect = React.lazy(
  () =>
    import(
      /* webpackChunkName: "scheduler/ServicePage" */ "../AutoSelect/AutoSelect"
    )
);

const LazyRenderSubTitle = React.lazy(
  () =>
    import(
      /* webpackChunkName: "scheduler/ServicePage" */ "../SubTitle/Subtitle"
    )
);
const LazyRenderDescription = React.lazy(
  () =>
    import(
      /* webpackChunkName: "scheduler/ServicePage" */ "../Description/Description"
    )
);

const LazyRenderEstimateLawnSize = React.lazy(
  () =>
    import(/* webpackChunkName: "scheduler/ServicePage" */ "./EstimateLawnSize")
);

const LazyRenderServiceWithCheckbox = React.lazy(
  () =>
    import(
      /* webpackChunkName: "scheduler/ServicePage" */ "../ServiceListCheckbox/ServiceListCheckbox"
    )
);

const LazySubmitAndAddPaymentButton = React.lazy(
  () =>
    import(
      /* webpackChunkName: "scheduler/SubmitAndAddPaymentButton" */ "../../../feature/scheduler/components/SubmitAndAddPayment/SubmitAndAddPaymentButton"
    )
);

type FieldRendererProps = {
  field: Field;
  error?: string;
  errors?: { [key: string]: string };
  setErrors?: React.Dispatch<React.SetStateAction<{ [key: string]: string }>>;
  disabled?: boolean;
  handleChange?: (
    e: React.ChangeEvent<
      | HTMLInputElement
      | HTMLSelectElement
      | HTMLTextAreaElement
      | { name?: string; value: unknown }
    >
  ) => void;
  handleCheckBoxGroupIcon?: (fieldId: string, value: string[]) => void;
  handleAutoSelect?: (fieldId: string, value: string) => void;
  options?: DynamicMenuOptions[];
  handleEmailBlur;
};

const RenderInputField = (props: FieldRendererProps): JSX.Element => {
  const { field, error, handleChange, handleEmailBlur } = props;
  const { formValues, setFormValues } = useFormContext();
  const shouldHideField = useBusinessNameVisibility(field?.id);
  const wrappedHandleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (handleChange) handleChange(e);
    if (
      field?.id === AppConstants.BUSINESS_NAME &&
      e.target?.name === AppConstants.FIELD_ID_SERVICE_TYPE &&
      formValues?.businessname
    ) {
      const value = e.target?.value;
      if (
        value?.toLowerCase() === AppConstants?.RESIDENTIAL_TEXT?.toLowerCase()
      ) {
        setFormValues((prev) => ({
          ...prev,
          businessname: "",
        }));
      }
    }
  };

  // If the field should be hidden, return null
  if (shouldHideField) {
    return null;
  }

  return (
    <InputField
      field={field}
      error={error}
      handleChange={wrappedHandleChange}
      handleEmailBlur={handleEmailBlur}
    />
  );
};

//This is a key value map where key is the field type and value is the component or component renderer like renderInputField
export const formBuilderFieldRendererMap: Record<
  string,
  (props: FieldRendererProps) => JSX.Element
> = {
  text: RenderInputField,
  email: RenderInputField,
  number: RenderInputField,
  zipcode: RenderInputField,
  tel: RenderInputField,
  name: RenderInputField,
  fname: RenderInputField,
  lname: RenderInputField,
  address: RenderInputField,
  "license-number": RenderInputField,
  textarea: ({ field, error, handleChange }) =>
    SUSPENSE_WRAPPER(
      <LazyRenderTextArea
        field={field}
        error={error}
        handleFunction={handleChange}
      />
    ),
  "radio-group": ({ field, error, errors, setErrors, handleChange }) => (
    <RadioGroupHandler
      field={field}
      error={error}
      errors={errors}
      setErrors={setErrors}
      handleFunction={handleChange}
    />
  ),
  select: ({ field, error, setErrors, handleChange, options }) =>
    SUSPENSE_WRAPPER(
      <LazyRenderSelect
        field={field}
        error={error}
        setErrors={setErrors}
        handleFunction={handleChange}
        options={options}
      />
    ),
  "file-upload": ({ field, error, setErrors }) =>
    SUSPENSE_WRAPPER(
      field?.optimizedimage ? (
        <LazyRenderImageUploadFile
          field={field}
          error={error}
          setErrors={setErrors}
        />
      ) : (
        <LazyRenderUploadFile
          field={field}
          error={error}
          setErrors={setErrors}
        />
      )
    ),
  checkbox: ({ field, errors }) => (
    <CheckBoxField field={field} errors={errors} />
  ),
  nextArrow: ({ field, disabled }) => (
    <NextArrowField field={field} disabled={disabled} />
  ),
  checkboxgroup: ({ field, error, setErrors }) =>
    SUSPENSE_WRAPPER(
      <LazyRenderCheckBoxGroup
        checkboxField={field}
        error={error}
        setErrors={setErrors}
      />
    ),
  radiogroupimage: ({ field, error, errors, setErrors }) =>
    SUSPENSE_WRAPPER(
      <LazyRenderRadioGroupImage
        field={field}
        error={error}
        errors={errors}
        setErrors={setErrors}
      />
    ),
  "checkbox-group-icon": ({ field, error, handleCheckBoxGroupIcon }) =>
    SUSPENSE_WRAPPER(
      <LazyRenderCheckBoxGroupIcon
        multiSelection={field?.multiselect}
        field={field}
        error={error}
        handleFunction={handleCheckBoxGroupIcon}
      />
    ),
  "auto-select": ({ field, error, handleAutoSelect }) =>
    SUSPENSE_WRAPPER(
      <LazyRenderAutoSelect
        field={field}
        error={error}
        handleFunction={handleAutoSelect}
      />
    ),
  subtitle: ({ field }) =>
    SUSPENSE_WRAPPER(<LazyRenderSubTitle field={field} />),
  description: ({ field }) =>
    SUSPENSE_WRAPPER(<LazyRenderDescription field={field} />),
  estimateLawnSize: ({ field, error, handleChange }) =>
    SUSPENSE_WRAPPER(
      <LazyRenderEstimateLawnSize
        field={field}
        error={error}
        handleChange={handleChange}
      />
    ),

  serviceWithCheckbox: ({ field, error, handleCheckBoxGroupIcon }) =>
    SUSPENSE_WRAPPER(
      <LazyRenderServiceWithCheckbox
        field={field}
        error={error}
        handleFunction={handleCheckBoxGroupIcon}
      />
    ),

  submitAndPayment: ({ field, disabled }) => {
    return SUSPENSE_WRAPPER(
      <LazySubmitAndAddPaymentButton field={field} disabled={disabled} />
    );
  },
};
